import { WizardStep } from "../../ui/wizard"
import { NeutralButton } from "../../common/Buttons"
import AuditUnitDetails from "./AuditUnitDetails"
import { useState, useEffect } from "react"
import ImagePreview from "../../Dealer/NAP/ImagePreview"
import ImagePreviewGrid from "../../common/ImagePreviewGrid"
import InfoItem from "../../common/InfoItem"
import auditConditionEnum from "../../../constants/auditConditionEnum"
import auditLocationEnum from "../../../constants/auditLocationEnum"
import { FaEdit } from "react-icons/fa"
import Spinner from "../../common/Spinner"
import { conditionColors, isOnTheLot } from "../../../utilities/auditUtilities"
import { Field, Textarea } from '@chakra-ui/react'
import { colors } from "../../../utilities/colors"
import { unitKeyEnum, runAndDriveEnum } from "../../../constants/conditionReportEnums"

export default function AuditUnitConfirm({
  auditUnit,
  auditData,
  onComplete,
  goToStep,
  setIsEditing,
  setAuditData,
  auditUnitFiles,
  photoTitles,
  isConditionReport = false
}) {
  const [imageUrls, setImageUrls] = useState({})
  const [previewImage, setPreviewImage] = useState(null)
  const [isCompleting, setIsCompleting] = useState(false)

  useEffect(() => {
    const urls = {}
    Object.entries(auditData.images).forEach(([key, file]) => {
      if (file) {
        urls[key] = URL.createObjectURL(file)
      } else if (auditUnitFiles?.find((file) => file.attributes.name === key)) {
        urls[key] = auditUnitFiles?.find((file) => file.attributes.name === key).attributes.url
      }
    })
    setImageUrls(urls)
  }, [auditData.images, auditUnitFiles])

  // Condition reports add a mileage/keys/run-and-drive step between the condition step and the photos
  const firstPhotoStepIndex = isConditionReport ? 2 : 1

  const handleEditClick = (index) => {
    setIsEditing(true)
    goToStep(index)
  }

  return (
    <WizardStep>
      <div className="flex flex-col items-center justify-between pt-6 rounded w-full h-full">
        <div className="flex flex-col w-full md:w-4/5">
          <AuditUnitDetails auditUnit={auditUnit} />
          <div className="mx-2 mt-2 border-t border-grey-700">
            <InfoItem title="Condition">
              <div className="flex items-center">
                <span className={conditionColors(auditData.condition)}>
                  {auditConditionEnum[auditData.condition]}
                </span>
                <FaEdit
                  className="ml-2 text-sm"
                  onClick={() => handleEditClick(0)}
                  data-testid="edit-condition-icon"
                />
              </div>
            </InfoItem>
            {isConditionReport &&
              <>
                <InfoItem title="Mileage">
                  <div className="flex items-center">
                    {auditData.mileage ? Number(auditData.mileage).toLocaleString() : ''}
                    <FaEdit
                      className="ml-2 text-sm"
                      onClick={() => handleEditClick(1)}
                      data-testid="edit-condition-report-icon"
                    />
                  </div>
                </InfoItem>
                <InfoItem title="Keys?">
                  <div className="flex items-center">
                    {unitKeyEnum[auditData.key]}
                  </div>
                </InfoItem>
                <InfoItem title="Runs and Drives?">
                  <div className="flex items-center">
                    {runAndDriveEnum[auditData.run_and_drive]}
                  </div>
                </InfoItem>
              </>
            }
            <InfoItem title="Can Refloor?">
              <div className="flex items-center">
                {auditData.can_refloor === '1' ? 'Yes' : 'No'}
              </div>
            </InfoItem>
            <InfoItem title="Location?">
              <div className="flex items-center">
                {auditLocationEnum[auditData.current_location]}
              </div>
            </InfoItem>
            {!isOnTheLot(auditData.current_location) && auditData.location &&
              <InfoItem title={String(auditData.current_location) === '3' ? 'Auction' : 'Where'}>
                <div className="flex items-center">
                  {auditData.location}
                </div>
              </InfoItem>
            }
            <Field.Root id="notes" mb={2}>
              <Field.Label fontSize={'sm'} color={colors.grey300} htmlFor="notes">Notes</Field.Label>
              <Textarea
                id="notes"
                value={auditData.notes ?? ''}
                onChange={(e) => setAuditData({ ...auditData, notes: e.target.value })}
                bgColor={colors.grey900}
                borderRadius='md'
                _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
              />
            </Field.Root>

          </div>
        </div>

        <ImagePreviewGrid
          imageUrls={imageUrls}
          onClick={(key) => setPreviewImage(imageUrls[key])}
          onEditClick={(_index, photoTitle) => handleEditClick(photoTitles.indexOf(photoTitle) + firstPhotoStepIndex)}
          isEditable={(photoTitle) => photoTitles.includes(photoTitle)}
          gridCols={Object.keys(imageUrls).length > 2 ? "grid-cols-3" : "grid-cols-2"}
        />

        <NeutralButton
          style="w-4/5"
          text={isCompleting ?
            <Spinner color="grey-300" height="20px" width="20px" strokeWidth={6} /> :
            isConditionReport ? "Complete Condition Report" : "Update Unit Audit"
          }
          onClick={async () => {
            setIsCompleting(true)
            const requestSent = await onComplete()
            if (!requestSent) {
              setIsCompleting(false)
            }
          }}
        />
      </div>

      <div className="fixed top-0 left-0 z-50">
        {previewImage && <ImagePreview
          image={previewImage}
          onClose={() => setPreviewImage(null)}
        />}
      </div>
    </WizardStep>
  )
}